import { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
    getUserProfile,
    updateUserProfile,
    UserProfile,
} from '../../libs/profile-service'

interface Props {
    profileData?: UserProfile | null
    onProfileUpdate?: (updatedProfile: UserProfile) => void
}

const Wrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 30px;
`

const FieldsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
`

const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const Label = styled.label`
    font-size: 18px;
    color: #666666;
    font-weight: 400;
`

const Input = styled.input`
    padding: 20px 24px;
    font-size: 20px;
    border: none;
    border-radius: 8px;
    background-color: #f2f3f5;
    color: #000000;
    outline: none;

    &:focus {
        background-color: #e8e9ec;
    }
`

const Select = styled.select`
    padding: 20px 24px;
    font-size: 20px;
    border: none;
    border-radius: 8px;
    background-color: #f2f3f5;
    color: #000000;
    outline: none;
    appearance: none;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><path fill="%23666666" d="M5 7l5 5 5-5z"/></svg>');
    background-repeat: no-repeat;
    background-position: right 20px center;
    cursor: pointer;

    &:focus {
        background-color: #e8e9ec;
    }
`

const SaveButton = styled.button`
    width: fit-content;
    padding: 16px 40px;
    background-color: #1fa474;
    color: #ffffff;
    border: none;
    border-radius: 50px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    margin-top: 20px;

    &:hover {
        background-color: #188c5f;
    }

    &:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
    }
`

const LoadingMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: #666666;
    font-size: 18px;
`

const ErrorMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: #ff4444;
    font-size: 18px;
`

export default function MyProfile({ profileData, onProfileUpdate }: Props) {
    const [firstName, setFirstName] = useState('')
    const [surname, setSurname] = useState('')
    const [school, setSchool] = useState('')
    const [loading, setLoading] = useState(!profileData)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (profileData) {
            // Use profile data from parent
            setFirstName(profileData.first_name)
            setSurname(profileData.last_name)
            setSchool(profileData.school)
            setLoading(false)
        } else {
            // Fallback: fetch profile if not provided
            loadProfile()
        }
    }, [profileData])

    const loadProfile = async () => {
        try {
            setLoading(true)
            setError(null)
            const profile = await getUserProfile()
            setFirstName(profile.first_name)
            setSurname(profile.last_name)
            setSchool(profile.school)
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to load profile'
            )
            console.error('Error loading profile:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        try {
            setSaving(true)
            setError(null)
            const updatedProfile = await updateUserProfile({
                first_name: firstName,
                last_name: surname,
                school: school,
            })
            // Notify parent of the update
            if (onProfileUpdate) {
                onProfileUpdate(updatedProfile)
            }
            alert('Profile updated successfully!')
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to save profile'
            )
            console.error('Error saving profile:', err)
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return <LoadingMessage>Loading profile...</LoadingMessage>
    }

    if (error) {
        return (
            <Wrapper>
                <ErrorMessage>{error}</ErrorMessage>
                <SaveButton onClick={loadProfile}>Retry</SaveButton>
            </Wrapper>
        )
    }

    return (
        <Wrapper>
            <FieldsRow>
                <FieldContainer>
                    <Label>First Name</Label>
                    <Input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                    />
                </FieldContainer>
                <FieldContainer>
                    <Label>Surname</Label>
                    <Input
                        type="text"
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                    />
                </FieldContainer>
            </FieldsRow>

            <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
            </SaveButton>
        </Wrapper>
    )
}
